import type { ReactNode } from 'react'
import styles from './DefinitionList.module.css'

/**
 * Label and value pairs, for the detail pane beside a record.
 *
 * A real `<dl>`. The alternative everybody writes is a grid of divs with the
 * label in one and the value in the other, and it loses the only thing the
 * markup was carrying: the association between them. In a `dl` a screen reader
 * announces "Reference, KANO-42" as one unit and can move between terms; in a
 * pair of divs it reads six labels and six values in sequence and the reader
 * has to hold the pairing in their head. On a case detail pane, where half the
 * fields are dates that look alike, that is the difference between a readable
 * pane and a list of numbers.
 *
 * The pairs are wrapped in a `div` each, which HTML has permitted inside a `dl`
 * since 5.2 and which is what makes the two-column grid possible without
 * abandoning the element.
 *
 * `empty` exists because a field with no value is not the same as a field that
 * does not apply, and both are common here: a case with no assignee is waiting
 * for one, and a case with no closing reason is open. Rendering an empty string
 * makes the row look like a rendering fault; rendering an em-rule makes every
 * absent value look identical. The caller supplies the words.
 */

export interface Definition {
  term: string
  description: ReactNode
  /** A note under the value: provenance, a caveat, a unit. */
  hint?: ReactNode
}

export interface DefinitionListProps {
  items: readonly Definition[]
  /**
   * `columns` puts the term and its value side by side, which suits a narrow
   * detail pane. `stacked` puts the value under its term and suits a wide one,
   * where a side-by-side pair leaves a gulf of empty space between them.
   */
  layout?: 'columns' | 'stacked'
  /** Shown in place of a description that is null, undefined or empty. */
  empty?: ReactNode
  className?: string
}

export function DefinitionList({
  items,
  layout = 'columns',
  empty = null,
  className,
}: DefinitionListProps) {
  return (
    <dl
      className={[styles.list, layout === 'stacked' ? styles.stacked : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      {items.map((item) => {
        const value = isBlank(item.description) ? empty : item.description

        return (
          <div key={item.term} className={styles.pair}>
            <dt className={styles.term}>{item.term}</dt>
            <dd className={styles.description}>
              <span className={isBlank(item.description) ? styles.absent : undefined}>{value}</span>
              {item.hint ? <span className={styles.hint}>{item.hint}</span> : null}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/**
 * Blank means nothing was supplied. Zero and `false` are values a field can
 * legitimately hold, and treating them as missing is how a count of zero cases
 * becomes "not recorded".
 */
function isBlank(value: ReactNode): boolean {
  return value === null || value === undefined || value === ''
}
